/**
 * TRUS-716: flag an OPEN case whose assignee is no longer on the list.
 *
 * Maui LTRG's Assigned DCM is a curated select that stores the DCM's NAME. When
 * a DCM leaves, an admin removes the name from the list: closed cases keep
 * showing it (the stored text is rendered as-is), and an open case still holding
 * it needs someone new.
 *
 * Which cases count as open is configuration, not code: `reassign_rules` on the
 * field (com_fields.reassign_rules) uses the visibility_rules condition shape,
 * read as "the case is open when these hold". No rule means never flagged — the
 * same fail-closed reading as required_rules, so no other field or org changes.
 */

import type { FieldOption } from '@/services/api/members';
import { evaluateVisibilityOperator } from '@/components/forms/fieldVisibility';

export interface ReassignRules {
  logic?: 'AND' | 'OR';
  conditions?: { field: string; operator: string; value?: any }[];
}

interface FieldLike {
  field_options?: FieldOption[] | null;
  reassign_rules?: ReassignRules | null;
}

function isEmpty(value: any): boolean {
  return value === undefined || value === null || String(value).trim() === '';
}

/**
 * A stored single-select value that is not on the field's current option list —
 * e.g. a DCM who has been removed. Empty values and fields with no options are
 * never "off the list".
 */
export function isOffList(field: FieldLike, value: any): boolean {
  if (isEmpty(value)) return false;
  const opts = field.field_options;
  if (!Array.isArray(opts) || opts.length === 0) return false;
  const shown = String(value);
  return !opts.some((o: any) => String(typeof o === 'string' ? o : o?.value) === shown);
}

/** True when the field has a reassign rule, its value is off the list, and the case is open. */
export function needsReassignment(
  field: FieldLike,
  value: any,
  recordValues: Record<string, any> | null | undefined,
): boolean {
  const rules = field.reassign_rules;
  if (!rules || !Array.isArray(rules.conditions) || rules.conditions.length === 0) return false;
  if (!isOffList(field, value)) return false;
  const results = rules.conditions.map((c) =>
    evaluateVisibilityOperator(c.operator, recordValues?.[c.field], c.value),
  );
  return String(rules.logic ?? 'AND').toUpperCase() === 'OR' ? results.some(Boolean) : results.every(Boolean);
}
